import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  useLayoutEffect,
  useContext,
  ReactNode,
} from "react";
import { getProfile, getUserPreferences } from "../../api/usersApi";
import i18n from "../../i18n";
import { useKeycloak } from "@react-keycloak/web";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { UnauthorizedError } from "../../api/apiUtils";
import { Profile } from "./ObjectTypes";
import { useLocation } from "react-router-dom";
import { getAppInsights } from "../../appInsights.js";

interface ProfileContextProps {
  profile: Profile | null;
  profileLoading: boolean;
  fetchProfile: () => Promise<void>;
  theme: "light" | "dark";
  toggleTheme: () => void;
}

const ProfileContext = createContext<ProfileContextProps | undefined>(
  undefined,
);

export const useProfile = (): ProfileContextProps => {
  const context = useContext(ProfileContext);
  if (context === undefined) {
    throw new Error("useProfile must be used within a ProfileProvider");
  }
  return context;
};

interface ProfileProviderProps {
  children: ReactNode;
}

export const ProfileProvider: React.FC<ProfileProviderProps> = ({
  children,
}) => {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const { keycloak, initialized } = useKeycloak();
  const { t } = useTranslation();
  const location = useLocation();
  const [theme, setTheme] = useState<"light" | "dark">(() => {
    const savedTheme = localStorage.getItem("theme");
    return savedTheme === "dark" ? "dark" : "light";
  });
  const [language, setLanguage] = useState<string | null>(null);

  const toggleTheme = () => {
    const newTheme = theme === "dark" ? "light" : "dark";
    setTheme(newTheme);
    localStorage.setItem("theme", newTheme);
  };

  // Sync the `dark` class on <html> with the theme state. This must NOT run
  // ahead of the re-render (it used to be toggled inside toggleTheme) and must
  // NOT run after the browser paints: components that colour themselves from
  // JS rather than Tailwind — react-select via `useProfile().theme` — only
  // change on re-render, so any gap between the class flip and that render
  // paints one frame of the old fill on the new background (the dropdown
  // flashing white or grey mid-toggle). useLayoutEffect commits both in the
  // same frame.
  useLayoutEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
  }, [theme]);

  const fetchProfile = useCallback(async () => {
    try {
      const profile = await getProfile();
      if (!profile.is_active) {
        window.location.href = "/unauthorized";
      }
      if (profile.first_name === "" || profile.last_name === "") {
        if (window.location.href.includes("/users/accountCreation") === false) {
          window.location.href = `/users/accountCreation/${profile.user_id}`;
        } else toast.info(t("Please update your profile"));
      }
      // Set theme in localStorage (or any other mechanism)
      if (profile.theme) {
        localStorage.setItem("theme", profile.theme);
      }
      setProfile(profile);
      await getAppInsights(profile.email);
    } catch (error: any) {
      console.error(error);
      if (error instanceof UnauthorizedError) {
        window.location.href = "/unauthorized";
      } else toast.error(t("Failed to fetch profile"));
    } finally {
      setProfileLoading(false);
    }
  }, [t]);

  useEffect(() => {
    const isReviewImageRoute = /^\/review\/.*\/image\/.*$/.test(
      location.pathname,
    );
    if (
      initialized &&
      keycloak.authenticated &&
      location.pathname.includes("/unauthorized") === false &&
      isReviewImageRoute === false // This will not fetch the profile on individual image review routes
    )
      fetchProfile();
  }, [initialized, keycloak.authenticated, location.pathname, fetchProfile]);

  useEffect(() => {
    const changeLanguage = async (languageCode: string) => {
      await i18n.changeLanguage(languageCode);
      setLanguage(languageCode);
    };

    const fetchUserPreferences = async (user_id: string) => {
      try {
        const preferences = await getUserPreferences(user_id);
        if (
          preferences != null &&
          preferences?.data?.language_code !== language
        ) {
          await changeLanguage(preferences.data.language_code);
        }
      } catch (error: any) {
        toast.error(t("Failed to fetch user preferences"));
      }
    };

    if (profile && !language) {
      fetchUserPreferences(profile.user_id);
    }
  }, [profile]);

  return (
    <ProfileContext.Provider
      value={{
        profile,
        profileLoading,
        fetchProfile,
        theme,
        toggleTheme,
      }}
    >
      {children}
    </ProfileContext.Provider>
  );
};
